import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Animated, Platform, ScrollView } from 'react-native';
import styled from 'styled-components/native';
import elementIDs from '../../elementIDs';

const OFFSET = Platform.OS === 'web' ? 0 : 7;

const StyledContainer = styled.View<{
  size: 'small' | 'default';
  backgroundColor?: string;
  removeBorder?: boolean;
}>`
  width: 100%;
  height: 65px;
  ${p =>
    p.removeBorder
      ? ``
      : `
   border-radius: 50px;
   border-width: 1px;
   border-color: rgba(0, 0, 0, 0.6);
  `}
  padding: 0 7px;
  flex-direction: row;
  background: ${p => (p.backgroundColor ? p.backgroundColor : '#fff')};
`;

const StyledItem = styled.TouchableOpacity<{ selected: boolean }>`
  flex-grow: 1;
  padding: 0 16px;
  height: 100%;
  align-items: center;
  justify-content: center;
`;

const StyledAnimatedTabIndicator = styled(Animated.View)<{
  size: 'small' | 'default';
}>`
  position: absolute;
  height: ${p => (p.size === 'small' ? 32 : 52)}px;
  border-radius: 50px;
  background-color: #ffcaaa;
  align-self: center;
`;

const StyledItemtext = styled.Text`
  font-family: 'DMSans-Bold';
  font-size: 14px;
  line-height: 18.23px;
  color: black;
`;

interface HeaderTabViewProps {
  value?: string;
  backgroundColor?: string;
  tabs: string[];
  size?: 'small' | 'default';
  removeBorder?: boolean;
  onTabChange?: (index: number, tab: string) => void;
}

const HeaderTabView: React.FC<HeaderTabViewProps> = ({
  value,
  tabs,
  size = 'default',
  removeBorder,
  backgroundColor,
  onTabChange
}) => {
  let tabIndex = value ? tabs.indexOf(value) : 0;
  if (tabIndex === -1) tabIndex = 0;
  const scrollViewRef = useRef<ScrollView>(null);
  const currentSelectedIndex = useRef<number>();
  const [selectedTabIndex, setSelectedTabIndex] = useState(tabIndex);
  const translateX = useRef(new Animated.Value(OFFSET)).current;
  const [displayWidth, setDisplayWidth] = useState<number>(0);
  const [itemLayouts, setItemLayouts] = useState<
    { x: number; width: number }[]
  >(tabs.map(() => ({ x: 0, width: 0 })));
  const handleSlide = useCallback(
    (index: number) => {
      Animated.spring(translateX, {
        toValue: itemLayouts[index]?.x,
        bounciness: 1,
        useNativeDriver: Platform.OS === 'ios' || Platform.OS === 'android'
      }).start();
      setDisplayWidth(itemLayouts[index]?.width);
      scrollViewRef.current?.scrollTo({
        x: itemLayouts[index]?.x
      });
    },
    [itemLayouts, translateX]
  );
  useEffect(() => {
    currentSelectedIndex.current = selectedTabIndex;
    handleSlide(selectedTabIndex);
  }, [handleSlide, selectedTabIndex]);
  return (
    <StyledContainer
      size={size}
      removeBorder={removeBorder}
      backgroundColor={backgroundColor}
    >
      <ScrollView
        ref={scrollViewRef}
        overScrollMode='never'
        bounces={false}
        horizontal
        contentContainerStyle={{ flexGrow: 1, justifyContent: 'space-between' }}
      >
        <StyledAnimatedTabIndicator
          size={size}
          style={{
            width: displayWidth,
            transform: [
              {
                translateX
              }
            ]
          }}
        />
        {tabs.map((tab, index) => {
          const selected = index === selectedTabIndex;
          return (
            <StyledItem
              testID={
                tab === 'Active'
                  ? elementIDs.activeTabID
                  : tab === 'Past'
                    ? elementIDs.pastTabID
                    : tab === 'Favorites'
                      ? elementIDs.favoritesTabID
                      : tab === 'Join'
                        ? elementIDs.joinTabID
                        : tab === 'Earn'
                          ? elementIDs.earnTabID
                          : tab === 'Redeem'
                            ? elementIDs.redeemTabID
                            : elementIDs.createTabID
              }
              key={index.toString()}
              onLayout={event => {
                itemLayouts[index] = {
                  x: event.nativeEvent.layout.x,
                  width: event.nativeEvent.layout.width
                };
                setItemLayouts([...itemLayouts]);
              }}
              onPress={() => {
                if (index === selectedTabIndex) return;
                setSelectedTabIndex(index);
                onTabChange?.(index, tab);
              }}
              selected={selected}
              disabled={selected}
            >
              <StyledItemtext>{tab}</StyledItemtext>
            </StyledItem>
          );
        })}
      </ScrollView>
    </StyledContainer>
  );
};

export default HeaderTabView;
